import API from "@/api";
import moment from "@/utils/moment";

import couponMixin from "@/mixins/couponMixin";
import paymentValidators from "@/utils/validators/payments";
import orderChannel from "../../../components/_Shared/channels/order";
import paymentsChannel from "../../../components/_Shared/channels/payments";
import { captureException, addBreadcrumb } from "@sentry/browser";

const SET_PAYABLE = "SET_PAYABLE";
const SET_PRELOADER = "SET_PRELOADER";
const SET_CREDIT_CARDS = "SET_CREDIT_CARDS";
const SET_CREDIT_CARDS_PRELOADER = "SET_CREDIT_CARDS_PRELOADER";
const SET_PRELOADER_COUPON = "SET_PRELOADER_COUPON";
const SET_PAYMENT_REQUESTED = "SET_PAYMENT_REQUESTED";
const SET_MANUAL_UPDATE = "SET_MANUAL_UPDATE";
const CLEAR_MANUAL_UPDATE = "CLEAR_MANUAL_UPDATE";
const SET_PRELOADER_PAYMENT = "SET_PRELOADER_PAYMENT";
const SET_ACCOUNT_BALANCE = "SET_ACCOUNT_BALANCE";
const SET_ACCOUNT_BONUS = "SET_ACCOUNT_BONUS";
const SET_PAYMENT_TYPE = "SET_PAYMENT_TYPE";
const SET_PAYMENT_PROCESSING_MESSAGE = "SET_PAYMENT_PROCESSING_MESSAGE";
const SET_PAYMENT_DATA = "SET_PAYMENT_DATA";
const SET_ERROR_ON_PAYMENT = "SET_ERROR_ON_PAYMENT";
const SET_PAYMENT_APPROVED = "SET_PAYMENT_APPROVED";
const RESET_PAYMENT = "RESET_PAYMENT";
const RESET = "RESET";

const defaultPaymentLoaderMessage = {
  title: "Vamos lá!",
  message: "Hora de limpar a casa :)"
};

const defaultState = () => {
  return {
    preloader: false,
    preloaderCoupon: false,
    preloaderPayment: false,
    preloaderCreditCards: false,
    type: null,
    payable: null,
    creditCards: false,
    oldCreditCards: false,
    paymentType: null,
    paymentInfo: null,
    paymentLoaderMessage: defaultPaymentLoaderMessage,
    errorOnPayment: false,
    errorMessageOnPayment: null,
    paymentApproved: false,
    paymentRequested: false,
    setIntervalId: null,
    accountBalance: false,
    accountBonus: false
  };
};

const actions = {
  async getPayable({ commit, dispatch }, { id, type }) {
    try {
      const payable =
        type === "order"
          ? await API.Orders.show(id)
          : await API.Cycles.show(id);

      commit("SET_PAYABLE", { payable, type });

      return true;
    } catch (e) {
      dispatch("toast/setToast", { type: "error" }, { root: true });

      return false;
    }
  },

  async getStoredCreditCards({ commit, dispatch }) {
    commit(SET_CREDIT_CARDS_PRELOADER, true);

    try {
      const ccs = await dispatch("user/getCreditCards", null, { root: true });
      commit(SET_CREDIT_CARDS, ccs);
    } catch (e) {
      dispatch(
        "toast/setToast",
        {
          message:
            "Não foi possível buscar os cartões salvos. Atualize a página!",
          type: "error"
        },
        { root: true }
      );

      commit(SET_CREDIT_CARDS_PRELOADER, false);
    }

    commit(SET_CREDIT_CARDS_PRELOADER, false);
  },

  async refreshPayable({ state, dispatch }) {
    await dispatch("getPayable", { id: state.payable.id, type: state.type });
  },

  async checkCoupon({ state, commit }, couponName) {
    const coupon = couponName.trim();

    commit("SET_PRELOADER_COUPON", true);

    if (
      couponMixin.methods.isCouponByDayInvalid(
        coupon,
        state.payable.schedule_event.starts_at
      )
    ) {
      commit("SET_PRELOADER_COUPON", false);

      throw couponMixin.methods.invalidCouponByDayMessage(coupon);
    }

    try {
      const response = await API.Coupons.getCouponByName(coupon);

      commit("SET_PRELOADER_COUPON", false);

      return response;
    } catch (e) {
      commit("SET_PRELOADER_COUPON", false);

      throw e;
    }
  },

  async applyCoupon({ state, commit, dispatch }, couponId) {
    commit("SET_PRELOADER_COUPON", true);

    const data = {
      order: state.payable.id,
      coupon: couponId
    };

    try {
      await API.Orders.checkIfCouponApplies(data);
      await API.Orders.applyCoupon(data);
      await dispatch("getPayable", { id: state.payable.id, type: state.type });

      commit("SET_PRELOADER_COUPON", false);
    } catch (e) {
      commit("SET_PRELOADER_COUPON", false);

      throw "O cupom informado não é aplicavel ao pedido!";
    }
  },

  async removeCoupon({ state, commit, dispatch }) {
    commit("SET_PRELOADER_COUPON", true);

    try {
      await API.Orders.removeCoupon(state.payable.id);
      await dispatch("getPayable", { id: state.payable.id, type: state.type });

      commit("SET_PRELOADER_COUPON", false);
    } catch (e) {
      commit("SET_PRELOADER_COUPON", false);

      throw "O cupom não pode ser removido!";
    }
  },

  async setPaymentType({ commit }, type) {
    commit("SET_PAYMENT_TYPE", type);
  },

  async setPaymentData({ commit }, data) {
    commit("SET_PAYMENT_DATA", data);
  },

  async createOnGateway({ state, commit, dispatch }) {
    commit("SET_PRELOADER_PAYMENT", true);

    try {
      await API.Orders.createOnGateway(state.payable.id);

      async function checkOrder() {
        if (state.paymentRequested) return;

        const payable =
          state.type === "order"
            ? await API.Orders.show(state.payable.id)
            : await API.Cycles.show(state.payable.id);

        if (
          payable.status === "created_on_gateway" &&
          !state.paymentRequested
        ) {
          orderChannel.received({ data: { to_state: payable.status } });
        }
      }

      const manualUpdate = setInterval(checkOrder, 10000);

      commit("SET_MANUAL_UPDATE", manualUpdate);
    } catch (e) {
      await dispatch("setErrorOnPayment");
    }
  },

  async createPayment({ commit, state, dispatch, getters }) {
    commit("SET_PRELOADER_PAYMENT", true);
    commit("SET_PAYMENT_REQUESTED", true);

    commit("CLEAR_MANUAL_UPDATE");

    try {
      const payload = {
        data: state.paymentData,
        payable: state.payable,
        accountBalance: state.accountBalance,
        accountBonus: state.accountBonus
      };

      if (getters.isPaymentNewCreditCard) {
        await API.Payments.paymentViaCreditCard(payload);
      } else if (getters.isPaymentStoredCreditCard) {
        await API.Payments.paymentViaStoredCreditCard(payload);
      } else if (getters.isPaymentBoleto) {
        await API.Payments.paymentViaBoleto(payload);
      } else {
        return;
      }

      async function checkPayment() {
        try {
          const payable =
            state.type === "order"
              ? await API.Orders.show(state.payable.id)
              : await API.Cycles.show(state.payable.id);

          commit("SET_PAYABLE", { payable, type: state.type });

          paymentsChannel.received({
            data: { to_state: getters.latestPayment.status, detail: null }
          });
        } catch (e) {
          captureException(e);
        }
      }

      const manualUpdate = setInterval(checkPayment, 5000);

      commit("SET_MANUAL_UPDATE", manualUpdate);
    } catch (e) {
      addBreadcrumb({
        data: { payload, response: JSON.stringify(e.response.data) }
      });
      captureException(e);
      await dispatch("setErrorOnPayment");
    }
  },

  async updatePaymentMessage({ commit }, message) {
    commit("SET_PAYMENT_PROCESSING_MESSAGE", message);
  },

  async setErrorOnPayment({ commit }, message = null) {
    commit(CLEAR_MANUAL_UPDATE);
    commit(SET_ERROR_ON_PAYMENT, { status: true, message });
  },

  async setPaymentApprved({ commit, dispatch }) {
    await new Promise(resolve => setTimeout(resolve, 2500));

    await dispatch("refreshPayable");
    commit(SET_PAYMENT_APPROVED, true);
    commit(CLEAR_MANUAL_UPDATE);
  },

  async confirmOrderWithAccountBalance({ commit, dispatch, state }) {
    commit("SET_PRELOADER_PAYMENT", true);
    commit("SET_PAYMENT_REQUESTED", true);

    commit("CLEAR_MANUAL_UPDATE");

    try {
      await API.Payments.confirmOrderWithAccountBalance(state.payable.id);
    } catch (e) {
      console.log(e);
      captureException(e);
      await dispatch("setErrorOnPayment");
    }
  },

  async setAccountBalance({ commit }) {
    commit(SET_ACCOUNT_BALANCE);
  },

  async setAccountBonus({ commit }) {
    commit(SET_ACCOUNT_BONUS);
  },

  async resetPayment({ commit, dispatch }) {
    dispatch("refreshPayable");
    commit(RESET_PAYMENT);
  },

  async reset({ commit }) {
    commit(RESET);
  }
};

const mutations = {
  [SET_PAYABLE](state, { payable, type }) {
    state.payable = payable;
    state.type = type;
  },

  [SET_PRELOADER](state, loading) {
    state.preloader = loading;
  },

  [SET_CREDIT_CARDS](state, cards) {
    state.oldCreditCards = cards.filter(cards =>
      cards.payment_gateway_id.includes("CRC-")
    );
    state.creditCards = cards.filter(cards =>
      cards.payment_gateway_id.includes("CARD_")
    );
  },

  [SET_CREDIT_CARDS_PRELOADER](state, loading) {
    state.preloaderCreditCards = loading;
  },

  [SET_PRELOADER_COUPON](state, loading) {
    state.preloaderCoupon = loading;
  },

  [SET_PRELOADER_PAYMENT](state, loading) {
    state.preloaderPayment = loading;
  },

  [SET_PAYMENT_TYPE](state, paymentType) {
    state.paymentType = paymentType;
  },

  [SET_PAYMENT_DATA](state, data) {
    state.paymentData = data;
  },

  [SET_PAYMENT_PROCESSING_MESSAGE](state, message) {
    state.paymentLoaderMessage = message;
  },

  [SET_ERROR_ON_PAYMENT](state, { status, message }) {
    state.errorOnPayment = status;
    state.errorMessageOnPayment = message;
  },

  [SET_PAYMENT_APPROVED](state, status) {
    state.paymentApproved = status;
  },

  [SET_ACCOUNT_BALANCE](state) {
    state.accountBalance = !state.accountBalance;
  },

  [SET_ACCOUNT_BONUS](state) {
    state.accountBonus = !state.accountBonus;
  },

  [RESET_PAYMENT](state) {
    state.errorOnPayment = false;
    state.errorMessageOnPayment = null;
    state.paymentApproved = false;
    state.preloaderPayment = false;
    state.paymentInfo = null;
    state.paymentLoaderMessage = defaultPaymentLoaderMessage;
    state.paymentType = null;
    state.accountBalance = false;
    state.accountBonus = false;
    clearInterval(state.setIntervalId);
  },

  [SET_PAYMENT_REQUESTED](state, status) {
    state.paymentRequested = status;
  },

  [SET_MANUAL_UPDATE](state, setIntervalId) {
    state.setIntervalId = setIntervalId;
  },

  [CLEAR_MANUAL_UPDATE](state) {
    clearInterval(state.setIntervalId);
  },

  [RESET](state) {
    Object.assign(state, defaultState());
  }
};

const getters = {
  noPayable: state => {
    return !state.payable;
  },

  canShowPayment: state => {
    return state.payable && !state.preloader && !state.preloaderCoupon;
  },

  canShowPaymentOptions: state => {
    return !state.paymentType;
  },

  cantPayWithBoleto: state => {
    if (state.type === "cycle") {
      return false;
    }

    const startsAt = state.payable.schedule_event.starts_at;

    return !paymentValidators.canPayViaBoleto(state.payableType, startsAt);
  },

  canShowCouponPart: ({ payable, preloaderPayment }, getters) => {
    return payable.coupon || (getters.notCreatedOnGateway && !preloaderPayment);
  },

  notCreatedOnGateway: (_, getters) => {
    return getters.isPayableOrder && getters.isOrderScheduled;
  },

  payablePaid: state => {
    return (
      (state.payable && state.payable.status === "paid") ||
      (state.payable &&
        state.paymentType === "BOLETO" &&
        state.payable.status === "waiting") ||
      state.paymentApproved
    );
  },

  isPayableOrder: state => {
    return state.payable.type === "orders";
  },

  isPayableCycle: state => {
    return state.payable.type === "cycles";
  },

  isPaymentCreditCard: (_, getters) => {
    return getters.isPaymentNewCreditCard || getters.isPaymentStoredCreditCard;
  },

  isPaymentNewCreditCard: state => {
    return state.paymentType === "CREDIT_CARD";
  },

  isPaymentStoredCreditCard: state => {
    return state.paymentType === "STORED_CREDIT_CARD";
  },

  isPaymentBoleto: state => {
    return state.paymentType === "BOLETO";
  },

  isPaymentAccountBalance: state => {
    return state.paymentType === "ACCOUNT_BALANCE";
  },

  canPayWithAccountBalance: (state, _, rootState) => {
    if (state.type != "order") return false;
    if (state.preloaderPayment) return false;
    if (!rootState.user.user.payment_gateway_account.wallet) return false;

    return (
      rootState.user.user.payment_gateway_account.wallet.available_to_use >=
      state.payable.amount.total
    );
  },

  canPayWithAccountBonus: (state, _, rootState) => {
    if (state.type != "order") return false;
    if (state.preloaderPayment) return false;
    if (Boolean(state.payable.coupon)) return false;
    if (!rootState.user.user.payment_gateway_account.wallet) return false;

    return (
      rootState.user.user.payment_gateway_account.wallet.bonus <=
      state.payable.amount.total
    );
  },

  activePayment: state => {
    const paymentOrderStatus = [
      "authorized",
      "waiting",
      "in_analysis",
      "requested"
    ];

    let activePayment = null;

    paymentOrderStatus.find(status => {
      if (state.payable.payments.find(payment => payment.status === status)) {
        activePayment = state.payable.payments.find(
          payment => payment.status === status
        );

        return true;
      }

      return false;
    });

    return activePayment;
  },

  latestPayment: state => {
    const latestPayment = state.payable.payments.sort(
      (a, b) => moment(b.created_at) - moment(a.created_at)
    );

    return latestPayment.length > 0 ? latestPayment[0] : null;
  },

  isOrderScheduled: state => {
    return state.payable.status === "scheduled";
  }
};

export default {
  namespaced: true,
  state: defaultState(),
  actions: actions,
  mutations: mutations,
  getters: getters
};



// WEBPACK FOOTER //
// ./src/store/modules/payables/payable.js